import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Building2, Mail, Phone, MapPin, Lock } from 'lucide-react';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { useEstablishmentStore } from '../store/establishment';

const registerSchema = z.object({
  name: z.string().min(3, 'Nome deve ter no mínimo 3 caracteres'),
  email: z.string().email('Email inválido'),
  phone: z.string().min(11, 'Telefone inválido'),
  address: z.string().min(10, 'Endereço muito curto'),
  password: z.string().min(6, 'A senha deve ter no mínimo 6 caracteres'),
  type: z.enum(['restaurant', 'barbershop', 'store', 'other']),
});

type RegisterFormData = z.infer<typeof registerSchema>;

export function EstablishmentRegister() {
  const navigate = useNavigate();
  const { register: registerEstablishment, error: registerError } = useEstablishmentStore();
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema)
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setLoading(true);
      await registerEstablishment(data);
      navigate('/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-500 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Cadastro de Estabelecimento</h1>
          <p className="text-gray-600 mt-2">Crie sua conta para começar a fidelizar clientes</p>
        </div>

        {registerError && (
          <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg">
            {registerError}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <Input
            label="Nome do Estabelecimento"
            {...register('name')}
            error={errors.name?.message}
            icon={<Building2 className="w-5 h-5 text-gray-400" />}
          />

          <Input
            label="Email"
            type="email"
            {...register('email')}
            error={errors.email?.message}
            icon={<Mail className="w-5 h-5 text-gray-400" />}
          />

          <Input
            label="Telefone"
            {...register('phone')}
            error={errors.phone?.message}
            icon={<Phone className="w-5 h-5 text-gray-400" />}
          />

          <Input
            label="Endereço"
            {...register('address')}
            error={errors.address?.message}
            icon={<MapPin className="w-5 h-5 text-gray-400" />}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Estabelecimento
            </label>
            <select
              {...register('type')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="restaurant">Restaurante</option>
              <option value="barbershop">Barbearia</option>
              <option value="store">Loja</option>
              <option value="other">Outro</option>
            </select>
            {errors.type && (
              <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
            )}
          </div>

          <Input
            label="Senha"
            type="password"
            {...register('password')}
            error={errors.password?.message}
            icon={<Lock className="w-5 h-5 text-gray-400" />}
          />

          <Button type="submit" loading={loading} className="w-full">
            Cadastrar Estabelecimento
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/login')}
            className="text-purple-600 hover:text-purple-700 font-medium"
          >
            Já tem uma conta? Faça login
          </button>
        </div>
      </div>
    </div>
  );
}